import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Music2, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ArtistsPage = () => {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArtists = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/artists`);
        setArtists(response.data);
      } catch (error) {
        console.error('Error fetching artists:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArtists();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-dark pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-dark pt-24 pb-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3 block">
            Creators
          </span>
          <h1 className="text-4xl sm:text-5xl font-light tracking-tighter text-white mb-4">
            Featured Artists
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl">
            Discover talented artists from across the globe.
          </p>
        </div>

        {/* Artists Grid */}
        {artists.length === 0 ? (
          <div className="bg-slate-900/50 border border-white/10 p-12 rounded-xl text-center">
            <Users className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h2 className="text-xl font-medium text-white mb-2">No Artists Yet</h2>
            <p className="text-slate-400">
              Artists will appear here once they join the platform.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="artists-grid">
            {artists.map((artist) => (
              <Link
                key={artist.id}
                to={`/artists/${artist.id}`}
                className="group bg-gradient-card border border-white/10 rounded-xl overflow-hidden hover:border-emerald-500/30 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-emerald-500/10"
                data-testid={`artist-card-${artist.id}`}
              >
                {/* Avatar */}
                <div className="aspect-square bg-slate-800 overflow-hidden">
                  {artist.avatar_url ? (
                    <img
                      src={artist.avatar_url}
                      alt={artist.artist_name || artist.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-500/20 to-cyan-500/20">
                      <Users className="w-20 h-20 text-emerald-500/50" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-white group-hover:text-emerald-400 transition-colors">
                    {artist.artist_name || artist.name}
                  </h3>
                  {artist.bio && (
                    <p className="text-sm text-slate-400 mt-1 line-clamp-2">
                      {artist.bio}
                    </p>
                  )}
                  <div className="flex items-center gap-1 mt-3 text-slate-500 text-sm">
                    <Music2 className="w-4 h-4" />
                    <span>{artist.track_count || 0} tracks</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
